"use client"

import { useCallback, useEffect, useState } from "react"
import { Loader2, Plus } from "lucide-react"
import { useWallet } from "@/lib/wallet-provider"
import { useCrystals, SATS_PER_CRYSTAL } from "@/hooks/use-crystals"
import { internalizeIncomingPayment } from "@/lib/wallet"
import { signedFetch } from "@/lib/api-auth-client"
import { TrophyIcon, WalletIcon } from "@/components/icons/pixel-icons"
import {
  MenuDetailHeading,
  MenuEmptyState,
  MenuGroup,
  MenuNotice,
  MenuStatRow,
  MenuValueRow,
} from "../menu-primitives"

const CRYSTAL_PACKS = [100, 500, 2500]

const LEDGER_LABELS: Record<string, string> = {
  purchase: "Bought crystals",
  "spend:premium-tower": "Premium tower",
  "spend:hero-clone": "Hero clone",
  grant: "Granted",
}

interface LedgerEntry {
  delta: number
  reason: string
  txid: string | null
  createdAt: string
}

const CrystalIcon = () => (
  <img
    src="/images/ui/crystal.svg"
    alt=""
    width={18}
    height={18}
    style={{ imageRendering: "pixelated" }}
  />
)

/**
 * Crystals: balance, buying, and cashing out ladder earnings.
 *
 * Crystals are an off-chain balance, so "receive" means buying them with a
 * single BRC-29 payment and claiming accrued ladder sats back into the wallet -
 * there is no player-to-player transfer to expose.
 */
export const CrystalsSection = () => {
  const { status, identityKey, connect } = useWallet()
  const { balance, isPurchasing, buyCrystals, refresh } = useCrystals()

  const [purchaseError, setPurchaseError] = useState<string | null>(null)
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null)

  const [unclaimedSats, setUnclaimedSats] = useState<number | null>(null)
  const [isClaiming, setIsClaiming] = useState(false)
  const [claimMessage, setClaimMessage] = useState<string | null>(null)
  const [claimError, setClaimError] = useState<string | null>(null)

  const [ledger, setLedger] = useState<LedgerEntry[]>([])

  const isConnected = status === "connected" && !!identityKey

  const loadLedger = useCallback(async () => {
    if (!identityKey) {
      setLedger([])
      return
    }
    try {
      const res = await fetch(`/api/crystals/ledger?pubkey=${encodeURIComponent(identityKey)}`)
      if (res.ok) {
        const data = await res.json()
        setLedger(Array.isArray(data.entries) ? data.entries : [])
      }
    } catch {
      /* activity list just stays as-is */
    }
  }, [identityKey])

  useEffect(() => {
    loadLedger()
  }, [loadLedger])

  useEffect(() => {
    if (!identityKey) {
      setUnclaimedSats(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/api/ladder?pubkey=${encodeURIComponent(identityKey)}`)
        const data = await res.json()
        if (!cancelled && typeof data.unclaimedSats === "number") {
          setUnclaimedSats(data.unclaimedSats)
        }
      } catch {
        /* row shows a dash */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [identityKey])

  const handleBuy = async (crystals: number) => {
    setPurchaseError(null)
    setPurchaseSuccess(null)
    const result = await buyCrystals(crystals)
    if (result.ok) {
      setPurchaseSuccess(`Purchased ${crystals.toLocaleString()} crystals`)
      loadLedger()
    } else {
      setPurchaseError(result.error ?? "Purchase failed")
    }
  }

  const handleClaim = async () => {
    if (!identityKey || isClaiming) return
    setClaimError(null)
    setClaimMessage(null)
    setIsClaiming(true)
    try {
      const res = await signedFetch("/api/ladder/claim", {}, identityKey)
      const data = await res.json()
      if (!res.ok) {
        setClaimError(typeof data.error === "string" ? data.error : "Claim failed")
        return
      }
      if (data.status === "paid") {
        const accepted = await internalizeIncomingPayment({
          tx: data.tx as number[],
          senderIdentityKey: data.senderIdentityKey as string,
          derivationPrefix: data.derivationPrefix as string,
          derivationSuffix: data.derivationSuffix as string,
          description: "Pelf ladder payout",
        })
        setUnclaimedSats(0)
        if (accepted) setClaimMessage(`${Number(data.sats).toLocaleString()} sats claimed`)
        else setClaimError("Payout sent, but your wallet did not accept it yet.")
      } else if (data.status === "accrued") {
        setClaimMessage("Payouts are not enabled yet — your balance keeps accruing.")
      } else {
        setClaimMessage("Nothing to claim")
      }
    } catch {
      setClaimError("Claim failed")
    } finally {
      setIsClaiming(false)
    }
  }

  if (!isConnected) {
    return (
      <div>
        <MenuDetailHeading
          title="Crystals"
          blurb="Crystals pay for premium towers and hero clones mid-run, so no wallet prompt interrupts combat."
        />
        <MenuGroup title="Wallet" icon={<WalletIcon size={14} />}>
          <MenuValueRow label="Not connected" sublabel="Connect a BSV wallet to buy and manage crystals">
            <button
              type="button"
              onClick={() => connect()}
              className="rounded-full border border-amber-600/50 px-3 py-1.5 text-xs font-bold text-amber-200 hover:bg-amber-900/30 transition"
            >
              Connect
            </button>
          </MenuValueRow>
        </MenuGroup>
      </div>
    )
  }

  return (
    <div>
      <MenuDetailHeading
        title="Crystals"
        blurb="Crystals pay for premium towers and hero clones mid-run, so no wallet prompt interrupts combat."
      />

      <MenuGroup title="Balance" icon={<CrystalIcon />}>
        <MenuValueRow icon={<CrystalIcon />} label="Your crystals" sublabel={`${SATS_PER_CRYSTAL} sats each`}>
          <span className="text-lg font-bold text-amber-300 tabular-nums">
            {balance.toLocaleString()}
          </span>
        </MenuValueRow>
      </MenuGroup>

      <MenuGroup title="Buy crystals" icon={<Plus size={14} />}>
        <div className="px-4 py-3">
          <div className="grid grid-cols-3 gap-2">
            {CRYSTAL_PACKS.map((pack) => (
              <button
                key={pack}
                type="button"
                onClick={() => handleBuy(pack)}
                disabled={isPurchasing}
                className="flex flex-col items-center gap-1 bg-black/40 hover:bg-black/60 disabled:opacity-50 border border-amber-600/30 rounded-lg py-2.5 transition"
              >
                {isPurchasing ? (
                  <Loader2 size={16} className="animate-spin text-amber-300" />
                ) : (
                  <span className="flex items-center gap-1 text-amber-200 font-bold text-sm">
                    <Plus size={12} />
                    {pack.toLocaleString()}
                  </span>
                )}
                <span className="text-gray-400 text-[10px] tabular-nums">
                  {(pack * SATS_PER_CRYSTAL).toLocaleString()} sats
                </span>
              </button>
            ))}
          </div>
          {purchaseError && <MenuNotice tone="error">{purchaseError}</MenuNotice>}
          {purchaseSuccess && <MenuNotice tone="success">{purchaseSuccess}</MenuNotice>}
        </div>
      </MenuGroup>

      <MenuGroup title="Ladder earnings" icon={<TrophyIcon size={14} />}>
        <MenuValueRow
          icon={<TrophyIcon size={18} />}
          label="Unclaimed sats"
          sublabel="Paid out to your wallet on claim"
        >
          <span className="text-sm font-bold text-amber-300 tabular-nums">
            {unclaimedSats == null ? "—" : unclaimedSats.toLocaleString()}
          </span>
          <button
            type="button"
            onClick={handleClaim}
            disabled={isClaiming || !unclaimedSats}
            className="rounded-full border border-amber-600/50 px-3 py-1.5 text-xs font-bold text-amber-200 hover:bg-amber-900/30 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {isClaiming ? <Loader2 size={12} className="animate-spin" /> : "Claim"}
          </button>
        </MenuValueRow>
        {claimError && <div className="px-4 pb-3">{<MenuNotice tone="error">{claimError}</MenuNotice>}</div>}
        {claimMessage && (
          <div className="px-4 pb-3">{<MenuNotice tone="info">{claimMessage}</MenuNotice>}</div>
        )}
      </MenuGroup>

      <MenuGroup title="Recent activity">
        {ledger.length === 0 ? (
          <MenuEmptyState>No crystal activity yet</MenuEmptyState>
        ) : (
          ledger.map((entry, i) => (
            <MenuStatRow
              key={`${entry.createdAt}-${i}`}
              label={`${LEDGER_LABELS[entry.reason] ?? entry.reason} · ${new Date(
                entry.createdAt,
              ).toLocaleDateString()}`}
              value={
                <span className={entry.delta >= 0 ? "text-green-400" : "text-red-400"}>
                  {entry.delta >= 0 ? "+" : ""}
                  {entry.delta.toLocaleString()}
                </span>
              }
            />
          ))
        )}
      </MenuGroup>

      <button
        type="button"
        onClick={() => {
          refresh()
          loadLedger()
        }}
        className="w-full text-xs text-gray-400 hover:text-amber-200 py-2 transition"
      >
        Refresh
      </button>
    </div>
  )
}
